import { Injectable, signal, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { PromotionCodeRequest, PromotionCodeResponse } from "../../shared/contract/PromoCode/PromoCode";
import { PromotionCodeState } from "../../shared/contract/Enum/PromoCode";
import { Observable, tap, catchError, throwError, switchMap, filter, finalize } from "rxjs";
import { QuotaAccount } from "src/app/shared/contract/PromoCode/QuotaAccount";
import { UserReceptionRequest } from "../../shared/contract/Reception/Reception";
import { InitService } from "src/app/shared/services/init.service";
import { NotificationService } from "../../shared/services/notification.service";
import { PermissionService } from "src/app/modules/core/services/permission.service";
import { DataModeService } from "../menu/data-mode.service";
import { MatDialog } from "@angular/material/dialog";
import { LandingPageDialogComponent } from "./landing-page-dialog/landing-page-dialog.component";
@Injectable({
  providedIn: "root"
})
export class ReceptionService {
  private readonly http = inject(HttpClient);
  private readonly initService = inject(InitService);
  private readonly notificationService = inject(NotificationService);
  private readonly permissionService = inject(PermissionService);
  private readonly dataModeService = inject(DataModeService);
  private readonly matDialog = inject(MatDialog);

  isLoading = signal(false);
  errorMessage = signal<string>("");
  isInValid = signal<boolean>(false);

  private readonly setPromotionCodeUrl = "/api/shop/PromotionCode";
  private readonly packageOverviewUrl = "/api/shop/ActiveBooked";
  private readonly getUserReceptionUrl = "/api/Reception/GetUserReception";
  private readonly sendUserReceptionMailUrl = "/api/Reception/SendUserReceptionMail";

  redeemCode(code: string): void {
    this.isLoading.set(true);
    this.errorMessage.set("");
    this.isInValid.set(false);

    const request = new PromotionCodeRequest();
    request.PromotionCode = code;

    this.setPromotionCode(request)
      .pipe(
        tap((response: PromotionCodeResponse) => {
          if (response.PromotionCodeState !== PromotionCodeState.Ok) {
            this.errorMessage.set("Exception.Failure.PromoCode.CodeState." + response.PromotionCodeState);
            this.isInValid.set(true);
          } else {
            this.notificationService.defaultSuccess();
          }
        }),
        filter((response: PromotionCodeResponse) => response.PromotionCodeState === PromotionCodeState.Ok),
        switchMap(() => this.permissionService.refreshQuota()),
        tap(() => this.closeDialog()),
        catchError(error => {
          this.errorMessage.set("Exception.Failure.PromoCode.General");
          this.isInValid.set(true);
          return throwError(() => error);
        }),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe();
  }

  getUserReception(): Observable<UserReceptionRequest> {
    this.isLoading.set(true);
    this.errorMessage.set("");

    return this.http.get<UserReceptionRequest>(this.initService.getApiUrl(this.getUserReceptionUrl)).pipe(
      catchError(error => {
        this.errorMessage.set("Exception.Failure.GetUserReception");
        this.isLoading.set(false);
        return throwError(() => error);
      })
    );
  }

  sendUserReceptionMail(userReception: UserReceptionRequest) {
    return this.http.post<UserReceptionRequest>(this.initService.getApiUrl(this.sendUserReceptionMailUrl), userReception);
  }

  setPromotionCode(promoCode: PromotionCodeRequest): Observable<PromotionCodeResponse> {
    return this.http.post<PromotionCodeResponse>(this.initService.getApiUrl(this.setPromotionCodeUrl), promoCode);
  }

  getPackageOverview() {
    return this.http.get<QuotaAccount[]>(this.initService.getApiUrl(this.packageOverviewUrl));
  }

  goToLandingPage() {
    this.matDialog.open(LandingPageDialogComponent);
  }

  closeDialog() {
    this.dataModeService.resetSelectedDataMode();
    this.matDialog.closeAll();
  }
}
